import { cn } from "@babybjorn/ui";
import type { CentPrecisionMoney } from "@commercetools/platform-sdk";
import { useTranslation } from "gatsby-plugin-react-i18next";
import type React from "react";
import { useAppState } from "../../context/app/context";
import { useCartRecommendations } from "../../hooks/use-cart-recommendations";
import { useCartService } from "../../hooks/use-cart-service";
import { useIsMinWidth } from "../../hooks/use-is-screensize";
import { formatMoney } from "../../utils/cart/currency";
import {
	getVariantColorLabelFromFragment,
	getVariantFabricLabelFromFragment,
	getVariantImages,
} from "../../utils/storyblok/product-variant";
import AddToCartButton from "../AddToCartButton";
import ImageContainer from "../general/ImageContainers";
import VariantList from "../general/VariantList";
import VariantListItem from "../general/VariantList/components/VariantListItem";
import MoreSalesList from "../MoreSalesList";
import MoreSalesBanner from "../MoreSalesList/components/MoreSalesBanner";
import * as s from "./CartRecommendations.module.scss";

export const CartRecommendations: React.FC<
	React.HTMLAttributes<HTMLDivElement> & { amount: number; cartModal?: boolean }
> = ({ amount, className, cartModal, ...props }) => {
	const { t } = useTranslation();
	const { cart } = useCartService();
	const { recommendations, status } = useCartRecommendations({ amount });

	if (
		(cart?.lineItems || []).length === 0 ||
		status !== "success" ||
		recommendations.length === 0
	) {
		return null;
	}

	return (
		<MoreSalesList
			heading={t("You might be interested in these?")}
			paddedItems={true}
			className={cn(className, { [s.moreSalesListInCartModal]: !!cartModal })}
			{...props}
		>
			{recommendations.map((recommendation) => (
				<CartRecommendationsItem
					key={recommendation.id}
					cartModal={cartModal}
					{...{ recommendation }}
				/>
			))}
		</MoreSalesList>
	);
};

const CartRecommendationsItem: React.FC<
	React.HTMLAttributes<HTMLLIElement> & {
		recommendation: Queries.CommerceToolsProduct;
		cartModal?: boolean;
	}
> = ({ recommendation, cartModal, ...props }) => {
	const { t } = useTranslation();
	const { siteConfig } = useAppState();
	const { addItemsToCart } = useCartService();
	const isMobile = useIsMinWidth(450);

	if (!recommendation.masterVariant) {
		return null;
	}

	// @ts-expect-error recommendation.masterVariant is Queries.CommerceToolsProductVariants,
	// but has the same structure to get images as Queries.ProductVariantFragment
	const image = getVariantImages(recommendation.masterVariant)?.at(0);

	if (!image) {
		return null;
	}

	const price = recommendation.masterVariant.prices.find(
		(price) => price.value.currencyCode === siteConfig.currency,
	)?.value as CentPrecisionMoney | undefined;

	if (!price) {
		return null;
	}

	const href =
		recommendation.masterVariant.storyblok_variant_pages?.at(0)?.full_slug;

	if (!href) {
		return null;
	}

	const fabric = getVariantFabricLabelFromFragment(
		// @ts-expect-error recommendation is Queries.CommerceToolsProduct
		recommendation.masterVariant.attributes,
	);

	const color = getVariantColorLabelFromFragment(
		// @ts-expect-error recommendation is Queries.CommerceToolsProduct
		recommendation.masterVariant.attributes,
	);

	const variantSymbolImages = [
		recommendation.masterVariant,
		...recommendation.variants,
	].map((variant) => variant.variantSymbolImage);

	const handleAddToCart = async () => {
		// addItemsToCart expects product: Queries.CommerceToolsProductFragment and variant: Queries.ProductVariantFragment
		// but we know that recommendation is Queries.CommerceToolsProduct and masterVariant is Queries.CommerceToolsProductVariants
		// so we cast them to the expected types
		await addItemsToCart([
			{
				product:
					recommendation as unknown as Queries.CommerceToolsProductFragment,
				variant:
					recommendation.masterVariant as unknown as Queries.ProductVariantFragment,
			},
		]);
	};

	return (
		<MoreSalesBanner
			key={recommendation.id}
			image={
				<ImageContainer
					imgSrc={image.src}
					alt={image.alt ?? ""}
					objectFit="cover"
				/>
			}
			productName={recommendation.name}
			price={formatMoney(price)}
			fabric={fabric}
			color={color}
			href={href}
			padding
			{...props}
		>
			<div className="flex gap-4 self-center">
				{variantSymbolImages.length > 0 && (
					<div className="flex gap-2">
						<VariantList
							variantAmount={Math.min(5, variantSymbolImages.length)}
						>
							{variantSymbolImages.slice(0, 5).map((image) => {
								if (!image?.src) {
									return null;
								}
								return (
									<VariantListItem
										key={image.src}
										image={{ src: image.src, alt: image.alt ?? "" }}
									/>
								);
							})}
						</VariantList>
						{variantSymbolImages.length > 5 && (
							<div className="bb-text-tiny text-gray-3">{`+${variantSymbolImages.length - 5}`}</div>
						)}
					</div>
				)}
			</div>

			<AddToCartButton
				onClick={handleAddToCart}
				buttonType={cartModal || !isMobile ? "icon" : "standard"}
				label={cartModal ? undefined : t("Add to cart")}
			/>
		</MoreSalesBanner>
	);
};
